import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input, Row, Col, Typography, Card, Alert } from "antd";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import { isAxiosError } from "axios";
import { auth } from "../../services/firebase";
import { setCookie } from "../../utils/cookies"; // Import setCookie
import CareHiresLogo from "../../assets/img/CH_New_Logo.png";
import MicrosoftLogo from "../../assets/img/microsoft.png";
import "./Login.css"; // Custom styles
import { getDomain } from "tldts";
import Toaster, { showToast } from "../Toaster/Toaster";
import {
  getAuth,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
  MultiFactorResolver,
  browserLocalPersistence,
  deleteUser,
  OAuthProvider,
  multiFactor,
  signInWithEmailAndPassword,
  UserCredential,
  signInWithPopup,
  getAdditionalUserInfo
} from "firebase/auth";
import { MFA_METHODS } from "../../utils/enum";
import SmsVerificationForm from "./MultiFactorAuthentication/sms/SmsVerificationForm";
import TotpVerificationForm from "./MultiFactorAuthentication/totp/TotpVerificationForm";
import OrganizationSwitcher from "../OrganizationSwitcher/OrganizationSwitcher";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getUserCompaniesByUID, getUserDataByUID, updateLastRequestTime, userSignOut, recordLastSignedIn } from "../../services/auth.service";
import { getDeviceFingerprint } from "../../utils/fingerprint";
import type { AccountFlaggedError } from "../../types/security";
import TwoFactorSelectionForm from "./MultiFactorAuthentication/TwoFactorSelectionForm";
import { datadogRum } from "@datadog/browser-rum";
import { isMobile } from "../../utils/device";
import { getUserImages } from "../../services/common.service";
import MFASetupReminder from "../MFASetupReminder/MFASetupReminder";
import { getMFAReminderSnoozed, snoozeMFAReminder } from "../MFASetupReminder/mfaReminder";

const firebaseAuth = getAuth();

const { Title, Text, Link } = Typography;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [userCompanies, setUserCompanies] = useState<any>(null);
  const [expiresInDays, setExpiresInDays] = useState<number>(0);
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  const cookieName = "ch_token";
  const [mfaMethod, setMfaMethod] = useState<
    null | "choose-method" | "totp" | "phone"
  >(null);
  const [showOrganizationSwitcher, setShowOrganizationSwitcher] = useState(false);
  const [shouldDisplayOrganizationSwitcherAfterLogin, setShouldDisplayOrganizationSwitcherAfterLogin] = useState(false);
  const [redirectToMfaSetupAfterLogin, setRedirectToMfaSetupAfterLogin] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const [name, setName] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [blockedDevice, setBlockedDevice] = useState(false);
  const [accountFlagged, setAccountFlagged] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const enForceMFAAuthRef = useRef(false);

  const handleWorkerOrMobileLogin = () => {
    if (isMobile()) {
      showToast("Id app doesn't support access from mobile devices.", {
        type: "error",
      });
      setLoading(false);
      setTimeout(() => {
        userSignOut();
      }, 3000);
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (searchParams.get("reason") === "flagged") {
      setAccountFlagged(true);
    }

    const queryParams = new URLSearchParams(window.location.search);
    const redirectUrlParam = queryParams.get("redirectUrl");
    if (redirectUrlParam) {
      setRedirectUrl(redirectUrlParam);
      sessionStorage.setItem("redirectUrl", redirectUrlParam); // Store in session storage
    } else {
      const storedRedirectUrl = sessionStorage.getItem("redirectUrl");
      if (storedRedirectUrl) {
        setRedirectUrl(storedRedirectUrl);
      } else {
        // setRedirectUrl(`${import.meta.env.VITE_SCM_DASHBOARD_URL}`);
      }
    }
  }, []);

  const extractPageName = (url: string) => {
    try {
      const path = new URL(url).pathname;
      return path.split("/").pop();
    } catch (error) {
      console.error("Error extracting page name from URL:", error);
      return "";
    }
  };

  const getDomainUrl = () => {
    return getDomain(window.location.href) || "";
  };

  const handleMFASetupReminder = async (userCredential: UserCredential, url: string, isExternal: boolean, showOrganizationSwitcher: boolean, isAdmin = false) => {
    if(isAdmin) {
      enForceMFAAuthRef.current = true;
    }
    const enrolledFactors = multiFactor(userCredential.user!).enrolledFactors;
    const hasMFASetup = enrolledFactors.length > 0;
    if (hasMFASetup) {
      // MFA already set up — go straight to dashboard
      if(isExternal) {
        window.location.href = url;
      } else {
        if(showOrganizationSwitcher) {
          setShowOrganizationSwitcher(true);
        } else {
          navigate(url);
        }
      }
    } else {
      const isSnoozed = await getMFAReminderSnoozed(userCredential.user!.uid);
      if (isSnoozed && !enForceMFAAuthRef.current) {
        if(isExternal) {
          window.location.href = url;
        } else {
          if(showOrganizationSwitcher) {
            setShowOrganizationSwitcher(true);
          } else {
            navigate(url);
          }
        }
      } else {
        setMfaModalOpen(true);
      }
    }
  };

  const handleSkip = async () => {
    if (userId) {
      await snoozeMFAReminder(userId);
    }
    setMfaModalOpen(false);
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      if(shouldDisplayOrganizationSwitcherAfterLogin) {
        setShowOrganizationSwitcher(true);
      } else {
        navigate("/dashboard")
      }
    }
  };

  const handleMFAClose = async () => {
    setMfaModalOpen(false);
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      if(shouldDisplayOrganizationSwitcherAfterLogin) {
        setShowOrganizationSwitcher(true);
      } else {
        navigate("/dashboard")
      }
    }
  };

  const handleSetupMFA = async () => {
    setMfaModalOpen(false);
    if(shouldDisplayOrganizationSwitcherAfterLogin) {
      setRedirectToMfaSetupAfterLogin(true);
      setShowOrganizationSwitcher(true);
    } else {
      navigate("/dashboard/Security");
    }
  };

  const onFinish = (values: any) => {
    setLoading(true);
    setUserEmail(values.email);
    firebaseAuth
      .setPersistence(browserLocalPersistence)
      .then(() => {
        return signInWithEmailAndPassword(auth, values.email, values.password);
      })
      .then((userCredential) => {
        completeLogin(userCredential);
      })
      .catch((error) => {
        console.error("Login error:", error);

        if (
          error.code === "auth/user-not-found" ||
          error.code === "auth/wrong-password"
        ) {
          showToast("You have entered an invalid username or password", {
            type: "error",
          });
        } else if (error.code === "auth/multi-factor-auth-required") {
          const multiFactorResolver = getMultiFactorResolver(
            firebaseAuth,
            error
          );
          const enrolledFactors = multiFactorResolver.hints.map(
            (info) => info.factorId
          );
          const mfaEnrolledDetails = multiFactorResolver.hints.map((info) => ({
            factorId: info.factorId,
            enrollmentId: info.uid
          }));
          setResolver(multiFactorResolver);
          localStorage.setItem("mfa_enrolled_factors", JSON.stringify(mfaEnrolledDetails))
          if (
            enrolledFactors.includes(TotpMultiFactorGenerator.FACTOR_ID) &&
            enrolledFactors.includes(
              PhoneMultiFactorGenerator.FACTOR_ID as "phone" | "totp"
            )
          ) {
            const phoneFactor: any = multiFactorResolver.hints.find(
              (factor) =>
                factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID
            );
            setUserPhone(phoneFactor?.phoneNumber);
            setMfaMethod("choose-method");
          } else if (
            enrolledFactors.includes(TotpMultiFactorGenerator.FACTOR_ID)
          ) {
            setMfaMethod("totp");
          } else if (
            enrolledFactors.includes(
              PhoneMultiFactorGenerator.FACTOR_ID as "phone" | "totp"
            )
          ) {
            const phoneFactor: any = multiFactorResolver.hints.find(
              (factor) =>
                factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID
            );
            setMfaMethod("phone");
            setUserPhone(phoneFactor?.phoneNumber);
          }
        } else {
          showToast(error.message, { type: "error" });
        }
        setLoading(false);
      });
  };

  const handleMicrosoftLogin = () => {
    const provider = new OAuthProvider("microsoft.com");
    firebaseAuth.setPersistence(browserLocalPersistence).then(() => {
      return signInWithPopup(auth, provider)
    }).then(async (response) => {
      const additionalUserInfo = getAdditionalUserInfo(response);
      if (additionalUserInfo?.isNewUser) {
        if(response.user)
          await deleteUser(response.user);

        showToast("Access denied. Please contact admin for account setup.", { type: "error" });
      }
        completeLogin(response, true);
      })
      .catch((error) => {
        console.error("Error during Microsoft login:", error);
        showToast("Error during Microsoft login", { type: "error" });
      });
  };

  const completeLogin = (userCredential: UserCredential, isSSOLoginAttempt = false) => {
    userCredential.user?.getIdTokenResult().then(async (idTokenResult) => {
      const token = idTokenResult.token;
      const expirationTime = new Date(idTokenResult.expirationTime);
      
      const expiresInDays =
        (expirationTime.getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24);
      setCookie("firebase_token", token, expiresInDays, getDomainUrl());
      setExpiresInDays(expiresInDays);
      try {
        const isWorker = idTokenResult.claims["worker"] == true;
        const isStaff = idTokenResult.claims["agency"] === true || idTokenResult.claims["provider"] === true;
        if(isWorker && !isStaff) {
          showToast("Access denied. Please contact admin for account setup.", { type: "error" });
        }
        const response = await updateLastRequestTime(token);

        if (response.success && response.data) {
          const user = userCredential.user?.toJSON() as {
            email: string | undefined;
            displayName: string | undefined;
            uid: string;
            stsTokenManager: { accessToken: string };
          };
          console.log("User data from token:", user);
          setName(user.displayName || "");
          setUserId(user.uid);

          // Start recording user sessions
          datadogRum.setUser({
            id: user.uid,
            name: user.displayName,
            email: user.email,
          });
          datadogRum.startSessionReplayRecording();

          // Security check: device fingerprint + login telemetry before issuing ch_token
          const fingerprint = await getDeviceFingerprint();
          try {
            const securityResponse = await recordLastSignedIn(user.uid, token, fingerprint);
            if (securityResponse.data?.deviceBlocked) {
              userSignOut();
              setBlockedDevice(true);
              setLoading(false);
              return;
            }
            if (!isSSOLoginAttempt && securityResponse.data?.passwordExpired && securityResponse.data?.passwordChangeToken) {
              showToast("Your password has expired. Please update your password.", { type: "warning" });
              userSignOut();
              setTimeout(() => {                
                navigate(`/set-password?token=${securityResponse.data?.passwordChangeToken}`);
              }, 3000);
              return;
            }
          } catch (securityError: unknown) {
            if (
              isAxiosError(securityError) &&
              securityError.response?.status === 403 &&
              (securityError.response.data as AccountFlaggedError).accountFlagged
            ) {
              userSignOut();
              setAccountFlagged(true);
              setLoading(false);
              return;
            }
            userSignOut();
            showToast("Unable to complete sign-in. Please try again.", { type: "error" });
            setLoading(false);
            return;
          }

          if (user) {
            const fetchResponse = await fetch(
              `${import.meta.env.VITE_FIREBASE_API_URL}/auth/access_token?uid=${user.uid}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            const data = await fetchResponse.json();

            if (data.success) {
              const accessToken = data.data;

              setCookie(cookieName, accessToken, expiresInDays, getDomainUrl());

              if (redirectUrl) {
                const userData = await getUserDataByUID(user.uid, token);

                if (userData.data.userGroup == "Provider") {
                  let companies: any = [];
                  const userCompaniesResponse = await getUserCompaniesByUID(
                    userData.data?._id,
                    "provider",
                    userData.data?.provider?._id,
                    token
                  );
                  const subCompanies = userCompaniesResponse?.data;
                  const providers = subCompanies
                    .filter((x: { provider: any }) => x.provider)
                    .map(
                      (company: {
                        provider: { id: any; companyName: any };
                        careHomes: any;
                        isAgreedToAgencyAndProviderTermsAndConditions: any;
                        isAgreedToCareHiresTermsAndConditions: any;
                        isAuthorizer: any;
                        isDefault: any;
                        isPrimaryUser: any;
                        jobTitle: any;
                        phone: any;
                        userType: any;
                        apps: any;
                      }) => ({
                        id: company.provider.id,
                        name: company.provider.companyName,
                        careHomes: company.careHomes,
                        isAgreedToAgencyAndProviderTermsAndConditions:
                          company.isAgreedToAgencyAndProviderTermsAndConditions,
                        isAgreedToCareHiresTermsAndConditions:
                          company.isAgreedToCareHiresTermsAndConditions,
                        isAuthorizer: company.isAuthorizer,
                        isDefault: company.isDefault,
                        isPrimaryUser: company.isPrimaryUser,
                        jobTitle: company.jobTitle,
                        phone: company.phone,
                        provider: company.provider,
                        userType: company.userType,
                        isProvider: true,
                        apps: company.apps,
                      })
                    );
                  if (providers.length > 0) {
                    const res = await getUserImages(
                      "provider",
                      providers.map((x: { id: any }) => x.id),
                      token
                    );
                    if (res.data.length > 0) {
                      for (const img of res.data) {
                        const provider = providers.find(
                          (x: { id: any }) => x.id === img.id
                        );
                        if (provider) {
                          provider.image = img.image;
                        }
                      }
                    }

                    companies = [...providers];
                    companies.forEach((c: any) => {
                      if (c.provider?._id === userData.data.provider?._id) {
                        c.isSelected = true;
                      } else {
                        c.isSelected = false;
                      }
                    });
                  }
                  localStorage.setItem("companies", JSON.stringify(companies));
                  localStorage.setItem(
                    "ch_user_data",
                    JSON.stringify(userData.data)
                  );
                  setCookie(
                    "ch_user_data",
                    JSON.stringify(userData.data),
                    expiresInDays,
                    getDomainUrl()
                  );
                } else if (userData.data.userGroup == "Agency") {
                  let companies: any = [];
                  const userCompaniesResponse = await getUserCompaniesByUID(
                    userData.data?._id,
                    "agency",
                    userData.data?.agency?._id,
                    token
                  );
                  const subCompanies = userCompaniesResponse?.data;
                  const agencies = subCompanies
                    .filter((x: { agency: any }) => x.agency)
                    .map(
                      (company: {
                        agency: { id: any; businessName: any };
                        locations: any;
                        isAgreedToAgencyAndProviderTermsAndConditions: any;
                        isAgreedToCareHiresTermsAndConditions: any;
                        isAuthorizer: any;
                        isDefault: any;
                        isPrimaryUser: any;
                        jobTitle: any;
                        phone: any;
                        userType: any;
                        apps: any;
                      }) => ({
                        id: company.agency.id,
                        name: company.agency.businessName,
                        locations: company.locations,
                        isAgreedToAgencyAndProviderTermsAndConditions:
                          company.isAgreedToAgencyAndProviderTermsAndConditions,
                        isAgreedToCareHiresTermsAndConditions:
                          company.isAgreedToCareHiresTermsAndConditions,
                        isAuthorizer: company.isAuthorizer,
                        isDefault: company.isDefault,
                        isPrimaryUser: company.isPrimaryUser,
                        jobTitle: company.jobTitle,
                        phone: company.phone,
                        agency: company.agency,
                        userType: company.userType,
                        isAgency: true,
                        apps: company.apps,
                      })
                    );
                  if (agencies.length > 0) {
                    const res = await getUserImages(
                      "agency",
                      agencies.map((x: { id: any }) => x.id),
                      token
                    );
                    if (res.data.length > 0) {
                      for (const img of res.data) {
                        const agency = agencies.find(
                          (x: { id: any }) => x.id === img.id
                        );
                        if (agency) {
                          agency.image = img.image;
                        }
                      }
                    }
                    companies = [...agencies];
                    companies.forEach((c: any) => {
                      if (c.agency?._id === userData.data.agency?._id) {
                        c.isSelected = true;
                      } else {
                        c.isSelected = false;
                      }
                    });
                  }
                  localStorage.setItem("companies", JSON.stringify(companies));
                  localStorage.setItem(
                    "ch_user_data",
                    JSON.stringify(userData.data)
                  );
                  setCookie(
                    "ch_user_data",
                    JSON.stringify(userData.data),
                    expiresInDays,
                    getDomainUrl()
                  );
                } else {
                  localStorage.setItem(
                    "ch_user_data",
                    JSON.stringify(userData.data)
                  );
                  setCookie(
                    "ch_user_data",
                    JSON.stringify(userData.data),
                    expiresInDays,
                    getDomainUrl()
                  );
                }
                // window.location.href = `${redirectUrl}/${pageName}`;
                // window.location.href = redirectUrl;
                const isAdmin = userData?.data?.userGroup == "Admin";
                handleMFASetupReminder(userCredential, redirectUrl, true, false, isAdmin);
                return;
              }

              // if (handleWorkerOrMobileLogin()) {
              //   return;
              // }

              const userData = await getUserDataByUID(user.uid, token);
              if (userData.data.userGroup == "Provider") {
                let companies: any = [];
                const userCompaniesResponse = await getUserCompaniesByUID(
                  userData.data?._id,
                  "provider",
                  userData.data?.provider?._id,
                  token
                );
                const subCompanies = userCompaniesResponse?.data;
                const providers = subCompanies
                  .filter((x: { provider: any }) => x.provider)
                  .map(
                    (company: {
                      provider: { id: any; companyName: any };
                      careHomes: any;
                      isAgreedToAgencyAndProviderTermsAndConditions: any;
                      isAgreedToCareHiresTermsAndConditions: any;
                      isAuthorizer: any;
                      isDefault: any;
                      isPrimaryUser: any;
                      jobTitle: any;
                      phone: any;
                      userType: any;
                      apps: any;
                    }) => ({
                      id: company.provider.id,
                      name: company.provider.companyName,
                      careHomes: company.careHomes,
                      isAgreedToAgencyAndProviderTermsAndConditions:
                        company.isAgreedToAgencyAndProviderTermsAndConditions,
                      isAgreedToCareHiresTermsAndConditions:
                        company.isAgreedToCareHiresTermsAndConditions,
                      isAuthorizer: company.isAuthorizer,
                      isDefault: company.isDefault,
                      isPrimaryUser: company.isPrimaryUser,
                      jobTitle: company.jobTitle,
                      phone: company.phone,
                      provider: company.provider,
                      userType: company.userType,
                      isProvider: true,
                      apps: company.apps,
                    })
                  );
                if (providers.length > 0) {
                  const res = await getUserImages(
                    "provider",
                    providers.map((x: { id: any }) => x.id),
                    token
                  );
                  if (res.data.length > 0) {
                    for (const img of res.data) {
                      const provider = providers.find(
                        (x: { id: any }) => x.id === img.id
                      );
                      if (provider) {
                        provider.image = img.image;
                      }
                    }
                  }

                  companies = [...providers];
                  companies.forEach((c: any) => {
                    if (c.provider?._id === userData.data.provider?._id) {
                      c.isSelected = true;
                    } else {
                      c.isSelected = false;
                    }
                  });
                }
                localStorage.setItem("companies", JSON.stringify(companies));
                if (userData.data?.provider?.parentCompany) {
                  setUserData(userData.data);
                  setUserCompanies(companies);
                  setShouldDisplayOrganizationSwitcherAfterLogin(true);
                  handleMFASetupReminder(userCredential, "/dashboard", false, true);

                } else {
                  localStorage.setItem(
                    "ch_user_data",
                    JSON.stringify(userData.data)
                  );
                  setCookie(
                    "ch_user_data",
                    JSON.stringify(userData.data),
                    expiresInDays,
                    getDomainUrl()
                  );
                  handleMFASetupReminder(userCredential, "/dashboard", false, false);
                }
              } else if (userData.data.userGroup == "Agency") {
                let companies: any = [];
                const userCompaniesResponse = await getUserCompaniesByUID(
                  userData.data?._id,
                  "agency",
                  userData.data?.agency?._id,
                  token
                );
                const subCompanies = userCompaniesResponse?.data;
                const agencies = subCompanies
                  .filter((x: { agency: any }) => x.agency)
                  .map(
                    (company: {
                      agency: { id: any; businessName: any };
                      locations: any;
                      isAgreedToAgencyAndProviderTermsAndConditions: any;
                      isAgreedToCareHiresTermsAndConditions: any;
                      isAuthorizer: any;
                      isDefault: any;
                      isPrimaryUser: any;
                      jobTitle: any;
                      phone: any;
                      userType: any;
                      apps: any;
                    }) => ({
                      id: company.agency.id,
                      name: company.agency.businessName,
                      locations: company.locations,
                      isAgreedToAgencyAndProviderTermsAndConditions:
                        company.isAgreedToAgencyAndProviderTermsAndConditions,
                      isAgreedToCareHiresTermsAndConditions:
                        company.isAgreedToCareHiresTermsAndConditions,
                      isAuthorizer: company.isAuthorizer,
                      isDefault: company.isDefault,
                      isPrimaryUser: company.isPrimaryUser,
                      jobTitle: company.jobTitle,
                      phone: company.phone,
                      agency: company.agency,
                      userType: company.userType,
                      isAgency: true,
                      apps: company.apps,
                    })
                  );
                if (agencies.length > 0) {
                  const res = await getUserImages(
                    "agency",
                    agencies.map((x: { id: any }) => x.id),
                    token
                  );
                  if (res.data.length > 0) {
                    for (const img of res.data) {
                      const agency = agencies.find(
                        (x: { id: any }) => x.id === img.id
                      );
                      if (agency) {
                        agency.image = img.image;
                      }
                    }
                  }
                  companies = [...agencies];
                  companies.forEach((c: any) => {
                    if (c.agency?._id === userData.data.agency?._id) {
                      c.isSelected = true;
                    } else {
                      c.isSelected = false;
                    }
                  });
                }
                localStorage.setItem("companies", JSON.stringify(companies));
                if (userData.data?.agency?.parentCompany) {
                  setUserData(userData.data);
                  setUserCompanies(companies);
                  setShouldDisplayOrganizationSwitcherAfterLogin(true);
                  handleMFASetupReminder(userCredential, "/dashboard", false, true);
                } else {
                  localStorage.setItem(
                    "ch_user_data",
                    JSON.stringify(userData.data)
                  );
                  setCookie(
                    "ch_user_data",
                    JSON.stringify(userData.data),
                    expiresInDays,
                    getDomainUrl()
                  );

                  handleMFASetupReminder(userCredential, "/dashboard", false, false);
                }
              } else if (userData.data.userGroup == "Admin") {
                localStorage.setItem(
                  "ch_user_data",
                  JSON.stringify(userData.data)
                );
                setCookie(
                  "ch_user_data",
                  JSON.stringify(userData.data),
                  expiresInDays,
                  getDomainUrl()
                );

                handleMFASetupReminder(userCredential, "/dashboard", false, false, true);
              }
            } else {
              showToast("Failed to get access token", { type: "error" });
              setLoading(false);
            }
          }
        }
      } catch (error) {
        console.error("Error updating last request time:", error);
      }
    });
  };

  return (
    <>
      <Toaster position="top-right" />
      {mfaMethod === MFA_METHODS.NONE && !showOrganizationSwitcher && !blockedDevice && !accountFlagged && (
        <>
          <div className="login-container" >
            <img
              className="login-title-main"
              src={CareHiresLogo}
              alt="CareHires Logo"
            />
            <Row justify="center" align="middle">
              <Card className="card-inline-height">
                <Row>
                  <Title level={2}>Sign in to your account</Title>
                </Row>
                <Row className="mb-2 main-submit-form-alignment">
                  <Form
                    onFinish={onFinish}
                    layout="vertical"
                    className="login-form"
                  >
                    <Form.Item
                      className="common-card-margin">
                      <Text className="placeholder-labels">Email</Text>
                    </Form.Item>
                    <Form.Item
                      className="common-card-margin"
                      name="email"
                      rules={[
                        { required: true, message: "Please input your email!" },
                      ]}
                    >
                      <Input
                        placeholder="Enter your Email"
                        className="custom-input"
                      />
                    </Form.Item>

                    <Form.Item
                      className="common-card-margin">
                      <Text className="placeholder-labels-password">Password</Text>
                    </Form.Item>
                    <Form.Item
                      className="common-card-margin"
                      name="password"
                      rules={[
                        {
                          required: true,
                          message: "Please input your password!",
                        },
                      ]}
                    >
                      <Input.Password
                        placeholder="Password"
                        className="custom-input"
                      />
                    </Form.Item>
                    <Form.Item className="common-card-margin">
                      <Title>
                        <Link className="forgot-password" href="/forgot-password">
                          Forgot Password?
                        </Link>
                      </Title>
                    </Form.Item>
                    <br />
                    <Form.Item className="common-card-margin">
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="login-button"
                        loading={loading}
                        style={{ width: '100%' }}
                      >
                        {loading ? "" : "Sign in"}
                      </Button>
                    </Form.Item>
                    <div className="divider">Or continue with</div>
                    <div className="button-container">
                      <Button
                        onClick={handleMicrosoftLogin}
                        className="microsoft-login"
                      >
                        <img src={MicrosoftLogo} width="24" alt="Microsoft logo" />
                        {/* Sign in with Microsoft */}
                      </Button>
                    </div>
                    <Text className="terms">
                      By clicking the button above, you agree to our{" "}
                      <Link
                        target="_blank"
                        href="https://carehires.com/terms-conditions"
                      >
                        terms of use
                      </Link>{" "}
                      and our{" "}
                      <Link
                        target="_blank"
                        href="https://carehires.com/privacy-policy"
                      >
                        privacy policy
                      </Link>
                      .
                    </Text>
                  </Form>
                </Row>
              </Card>
            </Row>
          </div >
        </>
      )}
      {
        showOrganizationSwitcher && (
          <div className="login-container">
            <OrganizationSwitcher
              expiresInDays={expiresInDays}
              show={showOrganizationSwitcher}
              userData={userData}
              userCompanies={userCompanies}
              isRedirectToMfaSetup={redirectToMfaSetupAfterLogin}
            />
          </div>
        )
      }
      {
        mfaMethod === MFA_METHODS.CHOOSE && (
          <TwoFactorSelectionForm
            resolver={resolver}
            userEmail={userEmail}
            userPhone={userPhone}
            redirectUrl={redirectUrl}
            extractPageName={extractPageName}
            handleWorkerOrMobileLogin={handleWorkerOrMobileLogin}
          />
        )
      }
      {
        mfaMethod === MFA_METHODS.PHONE && (
          <SmsVerificationForm
            resolver={resolver}
            userEmail={userEmail}
            userPhone={userPhone}
            redirectUrl={redirectUrl}
            extractPageName={extractPageName}
            handleWorkerOrMobileLogin={handleWorkerOrMobileLogin}
            onDeviceBlocked={() => { setBlockedDevice(true); setMfaMethod(null); }}
            onAccountFlagged={() => { setAccountFlagged(true); setMfaMethod(null); }}
          />
        )
      }
      {
        mfaMethod === MFA_METHODS.TOTP && (
          <TotpVerificationForm
            resolver={resolver}
            redirectUrl={redirectUrl}
            extractPageName={extractPageName}
            handleWorkerOrMobileLogin={handleWorkerOrMobileLogin}
            onDeviceBlocked={() => { setBlockedDevice(true); setMfaMethod(null); }}
            onAccountFlagged={() => { setAccountFlagged(true); setMfaMethod(null); }}
          />
        )
      }
      {blockedDevice && (
        <div className="login-container">
          <img className="login-title-main" src={CareHiresLogo} alt="CareHires Logo" />
          <Row justify="center" align="middle">
            <Card className="card-inline-height">
              <Alert
                type="error"
                showIcon
                message="Device Blocked"
                description={
                  <>
                    Your access from this device has been blocked. Please{" "}
                    <a href={import.meta.env.VITE_SUPPORT_DESK_URL} target="_blank" rel="noreferrer">
                      contact support
                    </a>
                    .
                  </>
                }
              />
            </Card>
          </Row>
        </div>
      )}
      {accountFlagged && (
        <div className="login-container">
          <img className="login-title-main" src={CareHiresLogo} alt="CareHires Logo" />
          <Row justify="center" align="middle">
            <Card className="card-inline-height">
              <Alert
                type="error"
                showIcon
                message="Account Flagged"
                description={
                  <>
                    Your account has been flagged for security reasons. Please{" "}
                    <a href={import.meta.env.VITE_SUPPORT_DESK_URL} target="_blank" rel="noreferrer">
                      contact support
                    </a>
                    .
                  </>
                }
              />
            </Card>
          </Row>
        </div>
      )}
      {
        <MFASetupReminder
          open={mfaModalOpen}
          onClose={handleMFAClose}
          onSetup={handleSetupMFA}
          onSkip={handleSkip}
          userName={name}
          enForceMFA={enForceMFAAuthRef.current}
        />
      }
    </>
  );
};

export default Login;
